import React, { useState } from 'react';
import axiosClient from 'http/axios-client';
import * as XLSX from 'xlsx';
import { Button } from '@material-ui/core';
import { useItems } from 'hooks/atoms/items-hook';
import AppLoader from 'components/basic/AppLoader';
import CollectionKeys from 'hooks/molecules/collection-keys';
import { parseDate } from 'components/business/consents/ConsentsTable';
import { FaNotesMedical } from 'react-icons/fa';
import CollectionItemHeader from 'components/layout/CollectionItemHeader';
import { LoadingButtonNoGrid } from 'components/basic/LoadingButton';
import SearchBarForLab from 'components/basic/SearchBarForLab';
import getTheme from 'theme/theme';
import AppTable from '../../basic/AppTable';
import { filterTypes } from '../../basic/AdvancedFilters';

const downloadData = async (setDownloadAllLoading, filterQuery, selected, initialFilter) => {
  setDownloadAllLoading(true);
  const theKits = await axiosClient.get(CollectionKeys.REGISTRATION_KITS, {
    params: {
      ...filterQuery,
      ...initialFilter,
    },
  });

  if (null == theKits) {
    setDownloadAllLoading(false);
    return;
  }

  setDownloadAllLoading(false);

  const data = [];
  theKits.forEach((kit) => {
    if (selected.includes(kit.id)) {
      let daysLeftRaw = 'No Received Date';
      const today = new Date();
      if (kit?.report_delivery_date !== null) {
        const deliveryDate = new Date(kit?.report_delivery_date);
        daysLeftRaw = Math.floor((deliveryDate - today) / (1000 * 60 * 60 * 24));
      }
      const daysLeft = Number.isFinite(daysLeftRaw) ? daysLeftRaw : 'No Received Date';

      const obj = {
        'Serial Number': kit.serial_number_id?.serial ?? ' ',
        'Thematic Package': kit.thematic_package?.thematic_category_id?.name ?? ' ',
        Status: kit?.registrationStatus,
        'Sample Received Date': kit?.metadata?.sampleReceivedDate
          ? parseDate(kit?.metadata?.sampleReceivedDate)
          : 'No Received Date',
        'Days Left': daysLeft,
        'Nucleic Acid (ng/uL)': kit?.serial_number_id?.metadata?.nucleic_acid,
      };

      data.push(obj);
    }
  });

  const worksheet = XLSX.utils.json_to_sheet(data);
  worksheet['!cols'] = fitToColumn(data);

  function fitToColumn(arrayOfArray) {
    if (!arrayOfArray[0]) { // If file is empty
      return null;
    }
    return Object.keys(arrayOfArray[0]).map((key) => {
      return { wch: key.length };
    });
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  XLSX.writeFile(workbook, 'Lab Reports.xlsx');
};

function LabCompletedSamplesTable() {
  const initialFilter = {
    'serial_number_id.sample_available_eq': true,
    'thematic_package.name_in': [ 'Nutritional kit', 'iDNA PGx kit', 'Vitamins kit' ],
    registrationStatus_eq: 'done',
    pdf_available_eq: true,
  };
  const [ filterQuery, setFilterQuery ] = useState({});
  const [ selected, setSelected ] = useState([]);
  const [ downloadLoading, setDownloadLoading ] = useState(false);

  const {
    items: kits,
    itemsLoading: kitsLoading,
    refetch,
    itemsError: kitsError,
    pagination,
  } = useItems(CollectionKeys.REGISTRATION_KITS, { filterQuery: {
    ...filterQuery,
    ...initialFilter,
  } });

  const {
    items: allKits,
    itemsLoading: allKitsLoading,
    itemsError: allKitsError,
  } = useItems(CollectionKeys.REGISTRATION_KITS, { filterQuery: {
    ...initialFilter,
    _limit: -1,
  } });

  if (kitsLoading || allKitsLoading) {
    return <AppLoader />;
  }

  const tableColumns = [
    { id: 1, label: 'Serial Number', field: 'serial_number_id.serial' },
    { id: 2, label: 'Thematic Package', field: 'thematic_package.name' },
    { id: 3,
      label: 'Sample Received Date',
      field: 'metadata.sampleReceivedDate',
      noSort: true,
      isDate: true,
      customRenderer({ row }) {
        let Rday = 'No Received Date';
        if (undefined !== row?.metadata?.sampleReceivedDate) {
          Rday = parseDate(row?.metadata?.sampleReceivedDate);
        }
        return (
          <div style={ { display: 'flex', justifyContent: 'center', gap: '5px' } }>
            {Rday}
          </div>
        );
      } },
    {
      id: 5,
      label: 'Nucleic Acid',
      noSort: true,
      field: 'serial_number_id?.metadata?.nucleic_acid',
      customRenderer({ row }) {
        return (
          <div style={ { display: 'flex', justifyContent: 'center', gap: '5px' } }>
            {row?.serial_number_id?.metadata?.nucleic_acid}
          </div>
        );
      },
    },
  ];

  if (kitsError) {
    return (
      <span>
        Error:
        {kitsError.message}
      </span>
    );
  }

  if (allKitsError) {
    return (
      <span>
        Error:
        {allKitsError.message}
      </span>
    );
  }

  return (
    <>
      <div style={ { display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: getTheme().spacing(2) } }>
        <FaNotesMedical
          style={ { color: getTheme().palette.secondary.dark, marginRight: getTheme().spacing(2) } }
          size={ 36 }
        />
        <CollectionItemHeader
          title='Completed Samples'
          subtitle='Nutritional kit, iDNA PGx kit, Vitamins kit'
          actions={ (
            <>
            </>
          ) }
        />
      </div>
      <div style={ { display: 'flex', gap: '5px', marginBottom: '15px' } }>
        <span>
          <Button
            variant='contained'
            color='secondary'
            onClick={ () => {
              setSelected(allKits?.map((ar) => { return ar.id; }));
            } }
          >
            Select All
          </Button>
        </span>
        <span>
          <Button
            variant='contained'
            color='secondary'
            onClick={ () => {
              setSelected([]);
            } }
          >
            Clear Selection
          </Button>
        </span>
        <LoadingButtonNoGrid
          variant='contained'
          onClick={ () => { downloadData(setDownloadLoading, filterQuery, selected, initialFilter); } }
          label='Excel Download'
          loading={ downloadLoading }
        />
        <SearchBarForLab setFilterQuery={ setFilterQuery } />
      </div>
      <AppTable
        columns={ tableColumns }
        data={ kits }
        refetch={ refetch }
        loading={ kitsLoading || allKitsLoading }
        paginationProps={ pagination }
        advancedFiltersProps={ {
          properties: [
            {
              value: 'thematic_package.name',
              label: 'Thematic Package',
              type: filterTypes.CATEGORY,
              options: [
                { value: 'iDNA PGx kit', label: 'PGx' },
                { value: 'Nutritional kit', label: 'Nutrition' },
                { value: 'Vitamins kit', label: 'Vitamins' },
              ],
            },
          ],
          initialAndFilterOptions: [],
          onQueryChange: setFilterQuery,
        } }
        selectionProps={ { setSelected, selected } }
      />
    </>
  );
}

export default LabCompletedSamplesTable;
